import type { BookmarkChange, BookmarkRow, PushResult, SyncStateRow } from '@bsync/shared';

/** Transport to the authoritative store. Implemented over Supabase in @bsync/client. */
export interface RemoteApi {
  /** Current server revision for the user; also a device heartbeat. */
  syncState(deviceId: string): Promise<SyncStateRow>;
  /** Apply changes atomically and return the authoritative rows for every touched id. */
  push(deviceId: string, changes: BookmarkChange[]): Promise<PushResult>;
  /** Rows with rev > sinceRev, ascending by rev. */
  pull(sinceRev: number, limit: number): Promise<BookmarkRow[]>;
}

export type RemoteErrorCode = 'offline' | 'unauthorized' | 'device_revoked' | 'rate_limited' | 'clock_skew' | 'server';

export class RemoteError extends Error {
  constructor(
    readonly code: RemoteErrorCode,
    message: string,
    override readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'RemoteError';
  }
  get retryable(): boolean {
    return this.code === 'offline' || this.code === 'rate_limited' || this.code === 'server' || this.code === 'clock_skew';
  }
}

export function asRemoteError(err: unknown): RemoteError {
  if (err instanceof RemoteError) return err;
  const message = err instanceof Error ? err.message : String(err);
  if (/fetch|network|Failed to fetch|NetworkError|ECONNREFUSED|timed? ?out/i.test(message)) {
    return new RemoteError('offline', message, err);
  }
  return new RemoteError('server', message, err);
}
